<template>
  <RACQFormKit :bindings="formInputData(step.formInputs?.hasAccessories?.formInputId?.value)" />

  <FormKit v-if="toBool(formData.hasAccessories)" type="list" name="accessories">
    <div class="flex flex-col gap-8 md:gap-6">
      <AddRemoveForm
        v-for="(_, index) in initialAccessories"
        :key="index"
        :title="{ value: `${accessoryItemLabel} ${index + 1}` }"
      >
        <template #buttonAction>
          <button type="button" class="link text-base-bold self-baseline" @click.prevent="removeAccessory(index)">
            {{ removeButtonText }}
          </button>
        </template>
        <template #sectionPanelContent>
          <FormKit
            v-if="renderInput(initialAccessories[index])"
            :options="filteredList(initialAccessories[index])"
            openOnClick
            clearSearchOnOpen
            :placeholder="dropdownPlaceholderText"
            validation="required"
            aria-label="Accessories List"
            title="Accessories List"
            outerClass="input-md !h-fit"
            wrapperClass="$reset md:block"
            inputClass="text-ellipsis whitespace-nowrap"
            :label="dropdownLabelText"
            :closeIcon="false"
            labelClass="hidden"
            :validationMessages="{ required: dropdownValidationErrorMessage }"
            type="autocomplete"
          />
        </template>
        <template #addButton>
          <FormKit
            v-if="showAddButton(index)"
            type="button"
            inputClass="btn btn-secondary btn-md input-cta mt-3"
            :label="addButtonText"
            @click="addAccessory"
          />
        </template>
      </AddRemoveForm>
    </div>
  </FormKit>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { IDropdownItem, IFormStep } from '@/interfaces';
import { IQConfigSettings, SettingCategory, useConfigSettingsStore, useReferenceDataIQMotorStore } from '@/store';
import { FormKit } from '@formkit/vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import AddRemoveForm from '@/components/AddRemoveForm/AddRemoveForm.vue';
import useFormInputData from '@/composables/useFormInputData';
import { toBool } from '@/utils/convert';
import { findFieldContent } from '@/utils/common-helper';

// Define props and emitters
// ------------------------------------------------------------- //
const props = withDefaults(
  defineProps<{
    step: IFormStep;
    initialAccessories?: string[];
  }>(),
  {
    initialAccessories: () => [''],
  }
);
const formData = defineModel<Record<string, any>>({ required: true });

// Define Store
// ------------------------------------------------------------- //
const refMotorStore = useReferenceDataIQMotorStore();
const configSettingsConfig = useConfigSettingsStore();

// Define Composables
// ------------------------------------------------------------- //
const { formInputData } = useFormInputData(props.step.formInputs!);

// Define Component state
// ------------------------------------------------------------- //
const initialAccessories = ref<string[]>(props.initialAccessories);
const list = ref<IDropdownItem[]>([]);
const maxAdditionalAccessories = ref(50);
const accessoryItemLabel = ref('');
const addButtonText = ref('');
const removeButtonText = ref('');
const dropdownLabelText = ref('');
const dropdownPlaceholderText = ref('');
const dropdownValidationErrorMessage = ref('');

const filteredList = computed(
  () => (self: string) =>
    list.value.filter((item) => item.value === self || !initialAccessories.value.includes(item.value))
);

const renderInput = computed(() => (self: string) => !self || list.value.some((item) => item.value === self));

const showAddButton = computed(() => (index: number) => {
  return (
    index === initialAccessories.value.length - 1 && initialAccessories.value.length < maxAdditionalAccessories.value
  );
});

watch(
  () => props.initialAccessories,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      initialAccessories.value = newValue;
    }
  }
);

onMounted(async () => {
  const [accessoryData, maxAccessoriesSetting] = await Promise.all([
    refMotorStore.getAccessoriesAndModification(),
    configSettingsConfig.getSetting(IQConfigSettings.MaxAdditionalAccessories, SettingCategory.IQSettings),
  ]);
  list.value = accessoryData.map((item) => ({ label: item.name, value: item.value }));
  maxAdditionalAccessories.value = (maxAccessoriesSetting as number) || 50;
  const fields = props.step.formInputs?.hasAccessories.fields || [];
  accessoryItemLabel.value = findFieldContent(fields, 'AccessoryItemLabel');
  addButtonText.value = findFieldContent(fields, 'AddButtonText');
  removeButtonText.value = findFieldContent(fields, 'RemoveButtonText');
  dropdownLabelText.value = findFieldContent(fields, 'DropdownLabelText');
  dropdownPlaceholderText.value = findFieldContent(fields, 'DropdownPlaceholderText');
  dropdownValidationErrorMessage.value = findFieldContent(fields, 'DropdownValidationErrorMessage');
});

function addAccessory() {
  initialAccessories.value.push('');
}

function removeAccessory(index: number) {
  const updatedAccessories = [...initialAccessories.value];
  updatedAccessories.splice(index, 1);

  // Ensure Vue detects the change
  initialAccessories.value = [];
  nextTick(() => {
    initialAccessories.value = updatedAccessories;
    formData.value.accessories = [...updatedAccessories];

    if (updatedAccessories.length === 0) {
      formData.value.hasAccessories = 'false';
    }
  });
}
</script>
